<template>
  <div class="pccd-page">
    <div class="page-header">
      <PageHeaderPortal>
        <div class="page-header-left">
        <div class="page-icon">
          <i class="fa-duotone fa-file-invoice-dollar"></i>
        </div>
        <div>
          <h1 class="page-title">Post Consummation DL Requests</h1>
          <p class="page-subtitle">Closing package received before funding — auto DL request queue and audit log</p>
        </div>
      </div>
      </PageHeaderPortal>

      <div class="header-actions">
        <select v-model="timeFrame" class="filter-select days-select" @change="loadData">
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="this_week">This Week</option>
          <option value="last_week">Last Week</option>
          <option value="last_7_days">Last 7 Days</option>
          <option value="last_30_days">Last 30 Days</option>
          <option value="last_90_days">Last 90 Days</option>
        </select>

        <button class="refresh-btn" @click="loadData" :disabled="loading">
          {{ loading ? 'Loading...' : 'Refresh' }}
        </button>
      </div>
    </div>

    <!-- Queue Status Bar -->
    <div class="queue-status-bar" v-if="queueStatus">
      <div class="queue-status-label">
        <i class="fa-duotone fa-database"></i>
        Live Queue
      </div>
      <div class="queue-pill pending">
        <span class="q-count">{{ queueStatus.pending }}</span>
        <span class="q-label">Pending</span>
      </div>
      <div class="queue-pill processing">
        <span class="q-count">{{ queueStatus.processing }}</span>
        <span class="q-label">Processing</span>
      </div>
      <div class="queue-pill done">
        <span class="q-count">{{ queueStatus.done }}</span>
        <span class="q-label">Done</span>
      </div>
      <div class="queue-pill errored">
        <span class="q-count">{{ queueStatus.error }}</span>
        <span class="q-label">Error</span>
      </div>
    </div>

    <!-- Summary Cards -->
    <div class="summary-sections">
      <div class="summary-section">
        <div class="section-header">
          <div class="section-title-wrap">
            <i class="fa-duotone fa-list-check section-icon"></i>
            <span class="section-title">Queue Overview</span>
          </div>
        </div>
        <div class="section-cards">
          <div class="summary-card">
            <div class="summary-card-header">
              <i class="fa-duotone fa-table-rows summary-icon"></i>
              <span class="summary-label">Total Processed</span>
            </div>
            <div class="summary-value">{{ summary.total }}</div>
          </div>
          <div class="summary-card">
            <div class="summary-card-header">
              <i class="fa-duotone fa-flask summary-icon"></i>
              <span class="summary-label">Dry Runs</span>
            </div>
            <div class="summary-value">{{ summary.dryRuns }}</div>
          </div>
        </div>
      </div>

      <div class="summary-section decisions-section">
        <div class="section-header">
          <div class="section-title-wrap">
            <i class="fa-duotone fa-sparkles section-icon"></i>
            <span class="section-title">Decisions</span>
          </div>
        </div>
        <div class="section-cards">
          <div class="summary-card approve">
            <div class="summary-card-header">
              <i class="fa-duotone fa-circle-check summary-icon"></i>
              <span class="summary-label">DL Request Sent</span>
            </div>
            <div class="summary-value">{{ summary.applied }}</div>
          </div>
          <div class="summary-card manual-review">
            <div class="summary-card-header">
              <i class="fa-duotone fa-hourglass-half summary-icon"></i>
              <span class="summary-label">Needs Review</span>
            </div>
            <div class="summary-value">{{ summary.pendingReview }}</div>
          </div>
          <div class="summary-card request-only">
            <div class="summary-card-header">
              <i class="fa-duotone fa-eye summary-icon"></i>
              <span class="summary-label">Already Checked</span>
            </div>
            <div class="summary-value">{{ summary.alreadyChecked }}</div>
          </div>
          <div class="summary-card no-audit">
            <div class="summary-card-header">
              <i class="fa-duotone fa-triangle-exclamation summary-icon"></i>
              <span class="summary-label">Errors</span>
            </div>
            <div class="summary-value">{{ summary.errors }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="toolbar single-line-toolbar">
      <input
        v-model.trim="search"
        type="text"
        class="search-input"
        placeholder="Search loan #, borrower, decision, notes..."
      />
      <select v-model="decisionFilter" class="filter-select toolbar-select">
        <option value="">All Decisions</option>
        <option value="pending_review">Needs Review</option>
        <option value="auto_applied">Auto Applied</option>
        <option value="manually_applied">Manually Applied</option>
        <option value="already_checked">Already Checked</option>
        <option value="no_trigger">No Trigger</option>
        <option value="insufficient_data">Insufficient Data</option>
        <option value="excluded_channel">Excluded (Brokered)</option>
        <option value="error">Error</option>
      </select>
      <select v-model="dryRunFilter" class="filter-select toolbar-select">
        <option value="">Live + Dry Runs</option>
        <option value="live">Live Only</option>
        <option value="dry">Dry Runs Only</option>
      </select>
    </div>

    <!-- Table -->
    <div class="table-card">
      <div class="table-card-header">
        <div class="table-card-title-wrap">
          <i class="fa-duotone fa-clipboard-list-check table-card-icon"></i>
          <span class="table-card-title">PCCD Log</span>
        </div>
        <div class="table-card-count">{{ filteredRows.length }}</div>
      </div>

      <div v-if="loading" class="table-state">Loading PCCD log...</div>
      <div v-else-if="errorMessage" class="table-state error">{{ errorMessage }}</div>
      <div v-else-if="pagedRows.length === 0" class="table-state">No records found.</div>

      <div v-else class="table-wrap">
        <table class="locks-table">
          <thead>
            <tr>
              <th @click="setSort('processedatutc')">
                Processed <span>{{ sortIcon('processedatutc') }}</span>
              </th>
              <th @click="setSort('loannumber')">
                Loan # <span>{{ sortIcon('loannumber') }}</span>
              </th>
              <th @click="setSort('borrowername')">
                Borrower <span>{{ sortIcon('borrowername') }}</span>
              </th>
              <th @click="setSort('decision')">
                Decision <span>{{ sortIcon('decision') }}</span>
              </th>
              <th @click="setSort('closingpackagereceiveddate')">
                Closing Pkg Rcvd <span>{{ sortIcon('closingpackagereceiveddate') }}</span>
              </th>
              <th @click="setSort('fundingdate')">
                Funding Date <span>{{ sortIcon('fundingdate') }}</span>
              </th>
              <th @click="setSort('daysearly')">
                Days Early <span>{{ sortIcon('daysearly') }}</span>
              </th>
              <th>Box Checked</th>
              <th>Dry Run</th>
              <th>Details / Approve</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="row in pagedRows" :key="row.id">
              <td class="compact-cell">{{ formatDateTime(row.processedatutc) }}</td>

              <td>
                <div class="primary-text">{{ row.loannumber || '—' }}</div>
              </td>

              <td>{{ row.borrowername || '—' }}</td>

              <td>
                <span class="decision-pill" :class="decisionClass(row.decision)">
                  {{ decisionLabel(row.decision) }}
                </span>
              </td>

              <td class="compact-cell">{{ row.closingpackagereceiveddate ? formatDate(row.closingpackagereceiveddate) : '—' }}</td>
              <td class="compact-cell">{{ row.fundingdate ? formatDate(row.fundingdate) : '—' }}</td>
              <td class="compact-cell">{{ row.daysearly ?? '—' }}</td>

              <td class="compact-cell">
                <span class="check-badge" :class="badgeClass(row.fieldwritten)" :title="row.writefieldid || ''">
                  {{ row.fieldwritten ? 'Checked' : 'No' }}
                </span>
              </td>

              <td class="compact-cell">
                <span class="status-pill" :class="row.dryrun ? 'pending' : 'processed'">
                  {{ row.dryrun ? 'Dry' : 'Live' }}
                </span>
              </td>

              <td>
                <div class="row-actions">
                  <button class="summary-btn" @click="openDetailModal(row)">
                    Details
                  </button>
                  <button
                    v-if="row.decision === 'pending_review' && !row.dryrun"
                    class="approve-btn"
                    @click="openApproveModal(row)"
                  >
                    Approve
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="filteredRows.length" class="pagination-bar">
      <div class="pagination-info">
        Showing {{ pageStart }} to {{ pageEnd }} of {{ filteredRows.length }}
      </div>
      <div class="pagination-actions">
        <button class="page-btn" @click="prevPage" :disabled="page === 1">Prev</button>
        <span class="page-number">Page {{ page }} of {{ totalPages }}</span>
        <button class="page-btn" @click="nextPage" :disabled="page === totalPages">Next</button>
      </div>
    </div>

    <!-- Detail Modal -->
    <div v-if="showDetailModal" class="modal-backdrop" @click.self="closeDetailModal">
      <div class="modal-card">
        <div class="modal-header">
          <h3 class="modal-title">PCCD Evaluation Details</h3>
          <button class="close-btn" @click="closeDetailModal">✕</button>
        </div>

        <div class="modal-body">
          <div class="reason-meta">
            <div><strong>Loan #:</strong> {{ selectedRow.loannumber || selectedRow.loanid || '—' }}</div>
            <div><strong>Borrower:</strong> {{ selectedRow.borrowername || '—' }}</div>
            <div><strong>Decision:</strong> {{ decisionLabel(selectedRow.decision) }}</div>
            <div><strong>Processed:</strong> {{ formatDateTime(selectedRow.processedatutc) }}</div>
            <div><strong>Run Type:</strong> {{ selectedRow.dryrun ? 'Dry Run' : 'Live' }}</div>
            <div><strong>Queue Status:</strong> {{ selectedRow.queuestatus || '—' }}</div>
          </div>

          <div class="check-grid">
            <div class="check-row">
              <i class="fa-duotone check-icon fa-file-arrow-down check-na"></i>
              <span class="check-label">Closing Package Received</span>
              <span class="check-extra">{{ selectedRow.closingpackagereceiveddate ? formatDate(selectedRow.closingpackagereceiveddate) : '—' }}</span>
            </div>
            <div class="check-row">
              <i class="fa-duotone check-icon fa-money-check-dollar check-na"></i>
              <span class="check-label">Funding Date</span>
              <span class="check-extra">{{ selectedRow.fundingdate ? formatDate(selectedRow.fundingdate) : '—' }}</span>
            </div>
            <div class="check-row">
              <i class="fa-duotone check-icon fa-calendar-days check-na"></i>
              <span class="check-label">Days Early</span>
              <span class="check-extra">{{ selectedRow.daysearly ?? '—' }}</span>
            </div>
            <div class="check-row">
              <i class="fa-duotone check-icon"
                 :class="selectedRow.fieldwritten ? 'fa-circle-check check-pass' : 'fa-circle-minus check-na'"></i>
              <span class="check-label">Field Written</span>
              <span class="check-extra">{{ selectedRow.writefieldid || '—' }} {{ selectedRow.writevalue ? '= ' + selectedRow.writevalue : '' }}</span>
            </div>
          </div>

          <!-- Error -->
          <div v-if="selectedRow.error" class="section-block">
            <div class="block-title" style="color:#b24553"><i class="fa-duotone fa-bug"></i> Error</div>
            <div class="reason-content error-content">{{ selectedRow.error }}</div>
          </div>

          <!-- Approve shortcut from the detail modal too -->
          <div v-if="selectedRow.decision === 'pending_review' && !selectedRow.dryrun" class="section-block">
            <button class="approve-btn" @click="openApproveModal(selectedRow)">Approve DL Request</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Approve Modal -->
    <div v-if="showApproveModal" class="modal-backdrop" @click.self="closeApproveModal">
      <div class="modal-card approve-card">
        <div class="modal-header">
          <h3 class="modal-title">Approve DL Request</h3>
          <button class="close-btn" @click="closeApproveModal">✕</button>
        </div>

        <div class="modal-body">
          <div class="reason-meta">
            <div><strong>Loan #:</strong> {{ approveRow.loannumber || approveRow.loanid || '—' }}</div>
            <div><strong>Borrower:</strong> {{ approveRow.borrowername || '—' }}</div>
            <div><strong>Closing package received:</strong> {{ approveRow.closingpackagereceiveddate ? formatDate(approveRow.closingpackagereceiveddate) : '—' }} — funded {{ approveRow.fundingdate ? formatDate(approveRow.fundingdate) : '—' }}</div>
          </div>

          <p class="approve-copy">
            Checking the box sends a real DL request letter through Encompass. Preview first
            with a dry run, then confirm to write it for real.
          </p>

          <div v-if="approvePreview" class="section-block">
            <div class="block-title"><i class="fa-duotone fa-flask"></i> Preview Result</div>
            <div class="reason-content" :class="{ 'error-content': !!approvePreview.errormessage }">
              <div><strong>Decision:</strong> {{ decisionLabel(approvePreview.decision) }}</div>
              <div><strong>Would write:</strong> {{ approvePreview.writefieldid || '—' }} {{ approvePreview.writevalue ? '= ' + approvePreview.writevalue : '' }}</div>
              <div v-if="approvePreview.notes"><strong>Notes:</strong> {{ approvePreview.notes }}</div>
              <div v-if="approvePreview.errormessage"><strong>Error:</strong> {{ approvePreview.errormessage }}</div>
            </div>
          </div>

          <div v-if="approveResult" class="section-block">
            <div class="block-title" :style="approveResult.errormessage ? 'color:#b24553' : 'color:#2c7a4b'">
              <i class="fa-duotone" :class="approveResult.errormessage ? 'fa-bug' : 'fa-circle-check'"></i>
              {{ approveResult.errormessage ? 'Apply Failed' : 'Applied' }}
            </div>
            <div class="reason-content" :class="{ 'error-content': !!approveResult.errormessage }">
              <div><strong>Decision:</strong> {{ decisionLabel(approveResult.decision) }}</div>
              <div><strong>Wrote:</strong> {{ approveResult.writefieldid || '—' }} {{ approveResult.writevalue ? '= ' + approveResult.writevalue : '' }}</div>
              <div v-if="approveResult.errormessage"><strong>Error:</strong> {{ approveResult.errormessage }}</div>
            </div>
          </div>

          <div class="approve-actions">
            <button class="page-btn" :disabled="approveBusy" @click="runApprove(true)">
              {{ approveBusy ? 'Working...' : 'Preview (Dry Run)' }}
            </button>
            <button class="approve-btn confirm" :disabled="approveBusy" @click="runApprove(false)">
              {{ approveBusy ? 'Working...' : 'Confirm & Send' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeaderPortal from "@/views/PageHeaderPortal.vue"
import api from '@/api/api.service'
import auth from '@/authConfig.js'

export default {
  components: { PageHeaderPortal },
  name: 'PccdDashboard',
  data() {
    return {
      loading: false,
      errorMessage: '',
      rows: [],
      queueStatus: null,
      timeFrame: 'last_7_days',
      search: '',
      decisionFilter: '',
      dryRunFilter: '',
      sortBy: 'processedatutc',
      sortDir: 'desc',
      page: 1,
      pageSize: 100,
      showDetailModal: false,
      selectedRow: {},
      showApproveModal: false,
      approveRow: {},
      approveBusy: false,
      approvePreview: null,
      approveResult: null
    }
  },
  computed: {
    visibleRows() {
      return (this.rows || []).filter(x => this.matchesTimeFrame(x?.processedatutc))
    },
    summary() {
      const rows = this.visibleRows
      return {
        total:          rows.length,
        dryRuns:        rows.filter(x => !!x.dryrun).length,
        applied:        rows.filter(x => x.decision === 'auto_applied' || x.decision === 'manually_applied').length,
        pendingReview:  rows.filter(x => x.decision === 'pending_review').length,
        alreadyChecked: rows.filter(x => x.decision === 'already_checked').length,
        errors:         rows.filter(x => x.decision === 'error' || x.decision === 'insufficient_data' || !!x.error).length
      }
    },
    filteredRows() {
      let rows = [...this.visibleRows]

      if (this.search) {
        const term = this.search.toLowerCase()
        rows = rows.filter(x => {
          const haystack = [
            x.loanid,
            x.loannumber,
            x.borrowername,
            x.decision,
            x.writefieldid,
            x.writevalue,
            x.error
          ].filter(Boolean).join(' ').toLowerCase()
          return haystack.includes(term)
        })
      }

      if (this.decisionFilter) {
        rows = rows.filter(x => x.decision === this.decisionFilter)
      }

      if (this.dryRunFilter === 'dry') {
        rows = rows.filter(x => !!x.dryrun)
      } else if (this.dryRunFilter === 'live') {
        rows = rows.filter(x => !x.dryrun)
      }

      rows.sort((a, b) => {
        const aVal = this.sortValue(a, this.sortBy)
        const bVal = this.sortValue(b, this.sortBy)
        if (aVal === bVal) return 0
        if (aVal == null) return 1
        if (bVal == null) return -1
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return this.sortDir === 'asc' ? aVal - bVal : bVal - aVal
        }
        const r = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' })
        return this.sortDir === 'asc' ? r : -r
      })

      return rows
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize))
    },
    pagedRows() {
      const start = (this.page - 1) * this.pageSize
      return this.filteredRows.slice(start, start + this.pageSize)
    },
    pageStart() {
      if (!this.filteredRows.length) return 0
      return (this.page - 1) * this.pageSize + 1
    },
    pageEnd() {
      return Math.min(this.page * this.pageSize, this.filteredRows.length)
    }
  },
  watch: {
    search()         { this.page = 1 },
    decisionFilter() { this.page = 1 },
    dryRunFilter()   { this.page = 1 },
    timeFrame()      { this.page = 1 },
    filteredRows()   { if (this.page > this.totalPages) this.page = 1 }
  },
  async mounted() {
    await this.loadData()
  },
  methods: {
    getRequestDays() {
      if (this.timeFrame === 'today')        return 1
      if (this.timeFrame === 'yesterday')    return 2
      if (this.timeFrame === 'this_week')    return 7
      if (this.timeFrame === 'last_week')    return 14
      if (this.timeFrame === 'last_7_days')  return 7
      if (this.timeFrame === 'last_30_days') return 30
      if (this.timeFrame === 'last_90_days') return 90
      return 7
    },
    async loadData() {
      this.loading = true
      this.errorMessage = ''
      try {
        const tokenResponse = await auth.acquireTokenResponse()
        const token = tokenResponse.accessToken

        const [logRes, queueRes] = await Promise.all([
          api.GetPccdLog(this.getRequestDays(), token),
          api.GetPccdQueueStatus(token)
        ])

        this.rows        = logRes?.data?.data  || []
        this.queueStatus = queueRes?.data?.data || null
      } catch (e) {
        this.rows = []
        this.errorMessage =
          e?.response?.data?.message ||
          e?.message ||
          'Failed to load PCCD log.'
      } finally {
        this.loading = false
      }
    },

    // ── Decision helpers ────────────────────────────────────────────────────
    decisionLabel(decision) {
      if (decision === 'no_trigger')        return 'No Trigger'
      if (decision === 'pending_review')    return 'Needs Review'
      if (decision === 'auto_applied')      return 'Auto Applied'
      if (decision === 'manually_applied')  return 'Manually Applied'
      if (decision === 'already_checked')   return 'Already Checked'
      if (decision === 'insufficient_data') return 'Insufficient Data'
      if (decision === 'excluded_channel')  return 'Excluded (Brokered)'
      if (decision === 'error')             return 'Error'
      return decision || '—'
    },
    decisionClass(decision) {
      if (decision === 'auto_applied' || decision === 'manually_applied') return 'approve'
      if (decision === 'pending_review')    return 'manual-review'
      if (decision === 'already_checked')   return 'request-only'
      if (decision === 'no_trigger')        return 'no-audit'
      if (decision === 'excluded_channel')  return 'no-audit'
      if (decision === 'insufficient_data') return 'error-pill'
      if (decision === 'error')             return 'error-pill'
      return 'no-audit'
    },
    badgeClass(val) {
      if (val === true)  return 'badge-pass'
      if (val === false) return 'badge-fail'
      return 'badge-na'
    },

    // ── Formatting ──────────────────────────────────────────────────────────
    formatDateTime(value) {
      if (!value) return '—'
      return new Date(value).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      })
    },
    formatDate(value) {
      if (!value) return '—'
      const d = new Date(value)
      if (isNaN(d)) return value
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },

    // ── Sorting ─────────────────────────────────────────────────────────────
    sortValue(row, key) {
      if (key === 'processedatutc' || key === 'closingpackagereceiveddate' || key === 'fundingdate') {
        return row[key] ? new Date(row[key]).getTime() : null
      }
      if (key === 'dryrun') return row.dryrun ? 1 : 0
      return row[key]
    },
    setSort(key) {
      if (this.sortBy === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
        return
      }
      this.sortBy  = key
      this.sortDir = 'asc'
    },
    sortIcon(key) {
      if (this.sortBy !== key) return ''
      return this.sortDir === 'asc' ? '▲' : '▼'
    },

    // ── Time frame ──────────────────────────────────────────────────────────
    startOfDay(date)  { const d = new Date(date); d.setHours(0,0,0,0); return d },
    endOfDay(date)    { const d = new Date(date); d.setHours(23,59,59,999); return d },
    startOfWeek(date) {
      const d = this.startOfDay(date)
      const day = d.getDay()
      d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day))
      return d
    },
    endOfWeek(date) {
      const d = this.startOfWeek(date)
      d.setDate(d.getDate() + 6)
      d.setHours(23,59,59,999)
      return d
    },
    matchesTimeFrame(value) {
      if (!value) return false
      const rowDate = new Date(value)
      if (isNaN(rowDate)) return false
      const now = new Date()
      const todayStart = this.startOfDay(now)
      const todayEnd   = this.endOfDay(now)
      if (this.timeFrame === 'today')     return rowDate >= todayStart && rowDate <= todayEnd
      if (this.timeFrame === 'yesterday') {
        const y = new Date(todayStart); y.setDate(y.getDate() - 1)
        return rowDate >= this.startOfDay(y) && rowDate <= this.endOfDay(y)
      }
      if (this.timeFrame === 'this_week') return rowDate >= this.startOfWeek(now) && rowDate <= this.endOfWeek(now)
      if (this.timeFrame === 'last_week') {
        const twS = this.startOfWeek(now)
        const lwS = new Date(twS); lwS.setDate(lwS.getDate() - 7)
        const lwE = new Date(twS); lwE.setMilliseconds(-1)
        return rowDate >= lwS && rowDate <= lwE
      }
      if (this.timeFrame === 'last_7_days')  { const s = this.startOfDay(now); s.setDate(s.getDate()-6);  return rowDate >= s && rowDate <= todayEnd }
      if (this.timeFrame === 'last_30_days') { const s = this.startOfDay(now); s.setDate(s.getDate()-29); return rowDate >= s && rowDate <= todayEnd }
      if (this.timeFrame === 'last_90_days') { const s = this.startOfDay(now); s.setDate(s.getDate()-89); return rowDate >= s && rowDate <= todayEnd }
      return true
    },

    // ── Pagination ──────────────────────────────────────────────────────────
    prevPage() { if (this.page > 1) this.page-- },
    nextPage() { if (this.page < this.totalPages) this.page++ },

    // ── Detail modal ────────────────────────────────────────────────────────
    openDetailModal(row)  { this.selectedRow = row || {}; this.showDetailModal = true },
    closeDetailModal()    { this.showDetailModal = false; this.selectedRow = {} },

    // ── Approve modal ───────────────────────────────────────────────────────
    openApproveModal(row) {
      this.approveRow = row || {}
      this.approvePreview = null
      this.approveResult = null
      this.showDetailModal = false
      this.showApproveModal = true
    },
    closeApproveModal() {
      this.showApproveModal = false
      this.approveRow = {}
      this.approvePreview = null
      this.approveResult = null
    },
    async runApprove(dryRun) {
      this.approveBusy = true
      try {
        const tokenResponse = await auth.acquireTokenResponse()
        const token = tokenResponse.accessToken
        const loanId = this.approveRow.loanid
        const res = await api.ApplyPccdRequest(loanId, dryRun, token)
        const data = res?.data?.data

        if (dryRun) {
          this.approvePreview = data || { errormessage: res?.data?.message || 'No preview returned.' }
        } else {
          this.approveResult = data || { errormessage: res?.data?.message || 'No result returned.' }
          // Refresh the log in the background so the row's new decision shows up.
          this.loadData()
        }
      } catch (e) {
        const message = e?.response?.data?.message || e?.message || 'Request failed.'
        if (dryRun) this.approvePreview = { errormessage: message }
        else this.approveResult = { errormessage: message }
      } finally {
        this.approveBusy = false
      }
    }
  }
}
</script>

<style scoped>
.pccd-page {
  padding: 18px;
  background: #f7f8fb;
  min-height: calc(100vh - 90px);
  display: flex;
  flex-direction: column;
  color: #1f2a44;
}

/* ── Header ─────────────────────────────────────────────── */
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}
.page-header-left { display: flex; align-items: center; gap: 14px; }
.page-icon {
  width: 48px; height: 48px;
  border-radius: 5px;
  background: #123a71;
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px;
  flex: 0 0 auto;
}
.page-title  { margin: 0; font-size: 20px; font-weight: 700; color: #1f2a44; letter-spacing: -.01em; }
.page-subtitle { margin: 4px 0 0; font-size: 13px; color: #7b8397; font-weight: 500; }
.header-actions { display: flex; gap: 8px; align-items: center; }

/* ── Queue status bar ───────────────────────────────────── */
.queue-status-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f0f4fb;
  border: 1px solid #dde4f0;
  border-radius: 5px;
  padding: 8px 14px;
  margin-bottom: 12px;
  font-size: 13px;
}
.queue-status-label { color: #5f79c7; font-weight: 700; display: flex; align-items: center; gap: 6px; margin-right: 4px; }
.queue-pill {
  display: flex; align-items: center; gap: 5px;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid transparent;
}
.queue-pill.pending    { background: #f2f4f7; border-color: #e1e5ec; color: #6d7789; }
.queue-pill.processing { background: #edf4ff; border-color: #d7e5fd; color: #3d6fd1; }
.queue-pill.done       { background: #eef9f0; border-color: #d9eedf; color: #2c7a4b; }
.queue-pill.errored    { background: #fff0f1; border-color: #f0d1d5; color: #b24553; }
.q-count { font-size: 15px; font-weight: 800; }
.q-label { font-size: 11px; font-weight: 600; opacity: .75; }

/* ── Summary cards ──────────────────────────────────────── */
.summary-sections {
  display: grid;
  grid-template-columns: 0.7fr 1.6fr;
  gap: 12px;
  margin-bottom: 12px;
}
.summary-section {
  background: #fbfbfd;
  border: 1px solid #e7ebf2;
  border-radius: 5px;
  padding: 12px;
}
.decisions-section { background: #fcfbff; }
.section-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.section-title-wrap { display: flex; align-items: center; gap: 8px; }
.section-icon  { color: #5f79c7; font-size: 14px; }
.section-title { font-size: 14px; font-weight: 700; color: #28344f; }
.section-cards { display: grid; gap: 10px; }
.summary-section:first-child .section-cards { grid-template-columns: 1fr 1fr; }
.decisions-section .section-cards { grid-template-columns: repeat(4, 1fr); }
.summary-card {
  background: #fdfdfe;
  border: 1px solid #e8ebf2;
  border-radius: 5px;
  padding: 12px;
}
.summary-card.approve       { border-color: #d8eedf; background: #fbfefc; }
.summary-card.manual-review { border-color: #f2d7d9; background: #fffafb; }
.summary-card.request-only  { border-color: #f4e7c4; background: #fffdfa; }
.summary-card.no-audit      { border-color: #e8ebf2; background: #fbfbfd; }
.summary-card-header { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.summary-icon  { font-size: 13px; color: #6a7b9f; }
.summary-label { font-size: 12px; color: #7b8397; font-weight: 600; }
.summary-value { font-size: 22px; font-weight: 800; color: #1f2a44; line-height: 1.1; }

/* ── Toolbar ────────────────────────────────────────────── */
.toolbar { margin-bottom: 10px; }
.single-line-toolbar {
  display: grid;
  grid-template-columns: 1fr 200px 180px;
  gap: 8px;
  align-items: center;
}
.search-input,
.filter-select {
  height: 38px;
  border: 1px solid #dfe4ee;
  background: #fcfcfe;
  border-radius: 5px;
  padding: 0 14px;
  font-size: 13px;
  width: 100%;
  color: #23314f;
  outline: none;
  transition: border-color .15s, box-shadow .15s;
}
.search-input::placeholder { color: #94a0b8; }
.search-input:focus, .filter-select:focus {
  border-color: #cfd8e8;
  box-shadow: 0 0 0 2px rgba(49,93,181,.08);
}
.days-select { min-width: 140px; }

/* ── Buttons ────────────────────────────────────────────── */
.refresh-btn, .page-btn, .close-btn, .summary-btn {
  border: 1px solid #d9dfeb;
  border-radius: 5px;
  background: #fff;
  color: #22314f;
  height: 38px;
  padding: 0 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background .15s, border-color .15s;
}
.refresh-btn:hover, .page-btn:hover, .summary-btn:hover { background: #f4f7fc; border-color: #cfd8e8; }
.summary-btn { height: 30px; padding: 0 12px; font-size: 12px; color: #315db5; background: #f5f8fc; border-color: #d9e2f0; }
.refresh-btn:disabled, .page-btn:disabled, .summary-btn:disabled { opacity: .55; cursor: not-allowed; }

.row-actions { display: flex; gap: 6px; flex-wrap: wrap; }
.approve-btn {
  height: 30px; padding: 0 12px; font-size: 12px; font-weight: 700;
  border-radius: 5px; cursor: pointer;
  color: #9a6b11; background: #fff7e7; border: 1px solid #f5e3b5;
  transition: background .15s, border-color .15s;
}
.approve-btn:hover { background: #fdf0d0; }
.approve-btn.confirm { color: #fff; background: #2c7a4b; border-color: #256b41; height: 38px; padding: 0 16px; }
.approve-btn.confirm:hover { background: #256b41; }
.approve-btn:disabled { opacity: .55; cursor: not-allowed; }
.approve-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 10px; }
.approve-copy { font-size: 13px; color: #5f6b84; line-height: 1.6; margin: 0 0 14px; }

/* ── Table ──────────────────────────────────────────────── */
.table-card {
  background: #fbfbfd;
  border: 1px solid #e7ebf2;
  border-radius: 5px;
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.table-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid #e9edf3;
  background: #fafbfd;
}
.table-card-title-wrap { display: flex; align-items: center; gap: 8px; }
.table-card-icon  { color: #5f79c7; font-size: 14px; }
.table-card-title { font-size: 14px; font-weight: 700; color: #28344f; }
.table-card-count { font-size: 13px; font-weight: 700; color: #7b8397; }
.table-wrap { overflow: auto; flex: 1; }
.locks-table { width: 100%; border-collapse: separate; border-spacing: 0; min-width: 1200px; }
.locks-table th, .locks-table td {
  padding: 11px 12px;
  border-bottom: 1px solid #edf0f5;
  font-size: 13px;
  text-align: left;
  vertical-align: middle;
}
.locks-table th {
  background: #f7f9fc;
  color: #6f7b92;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  position: sticky;
  top: 0;
  z-index: 1;
}
.locks-table tbody tr { background: #fbfbfd; }
.locks-table tbody tr:hover { background: #f8faff; }
.primary-text { font-weight: 700; color: #24314f; }
.compact-cell { white-space: nowrap; }

/* ── Decision pills ─────────────────────────────────────── */
.decision-pill {
  display: inline-flex; align-items: center;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px; font-weight: 700;
  white-space: nowrap;
  border: 1px solid transparent;
}
.decision-pill.approve       { background: #eef9f0; border-color: #d9eedf; color: #2c7a4b; }
.decision-pill.manual-review { background: #fff0f1; border-color: #f0d1d5; color: #b24553; }
.decision-pill.request-only  { background: #fff7e7; border-color: #f5e3b5; color: #9a6b11; }
.decision-pill.no-audit      { background: #f2f4f7; border-color: #e1e5ec; color: #6d7789; }
.decision-pill.error-pill    { background: #fff0f1; border-color: #f0d1d5; color: #b24553; }

/* ── Status pills ───────────────────────────────────────── */
.status-pill {
  display: inline-flex; align-items: center;
  border-radius: 999px;
  padding: 3px 9px;
  font-size: 11px; font-weight: 700;
  border: 1px solid transparent;
}
.status-pill.processed { background: #edf4ff; border-color: #d7e5fd; color: #3d6fd1; }
.status-pill.pending   { background: #f2f4f7; border-color: #e1e5ec; color: #6d7789; }

/* ── Check badges ───────────────────────────────────────── */
.check-badge {
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 3px;
  padding: 2px 5px;
  font-size: 10px; font-weight: 700;
  border: 1px solid transparent;
}
.check-badge.badge-pass { background: #eef9f0; border-color: #d9eedf; color: #2c7a4b; }
.check-badge.badge-fail { background: #fff0f1; border-color: #f0d1d5; color: #b24553; }
.check-badge.badge-na   { background: #f2f4f7; border-color: #e1e5ec; color: #9da8bc; }

/* ── Pagination ─────────────────────────────────────────── */
.pagination-bar { display: flex; justify-content: space-between; align-items: center; padding: 10px 4px 0; gap: 12px; }
.pagination-info    { color: #7b8397; font-size: 13px; font-weight: 500; }
.pagination-actions { display: flex; align-items: center; gap: 8px; }
.page-number        { font-size: 13px; color: #5c6780; font-weight: 600; }

.table-state       { padding: 28px; text-align: center; color: #7b8397; font-size: 14px; }
.table-state.error { color: #b94b5c; }

/* ── Modal ──────────────────────────────────────────────── */
.modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(28,37,56,.32);
  display: flex; align-items: center; justify-content: center;
  padding: 24px; z-index: 1000;
}
.modal-card {
  width: min(740px, 100%);
  max-height: 85vh;
  background: #fcfcfe;
  border: 1px solid #e7ebf2;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 18px 44px rgba(28,37,56,.12);
  display: flex; flex-direction: column;
}
.modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid #e9edf3;
  background: #fafbfd;
  flex: 0 0 auto;
}
.modal-title { margin: 0; font-size: 16px; color: #22314f; font-weight: 700; }
.close-btn   { width: 36px; height: 36px; padding: 0; }
.modal-body  { padding: 18px; overflow-y: auto; flex: 1; }

.reason-meta {
  display: grid; gap: 5px;
  margin-bottom: 16px;
  color: #5f6b84; font-size: 13px;
}

/* ── Check grid in modal ────────────────────────────────── */
.check-grid { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; }
.check-row  { display: flex; align-items: center; gap: 8px; font-size: 13px; }
.check-icon { font-size: 15px; flex: 0 0 auto; }
.check-pass { color: #2c7a4b; }
.check-fail { color: #b24553; }
.check-na   { color: #9da8bc; }
.check-label { color: #28344f; font-weight: 600; }
.check-extra { color: #6a7b9f; font-size: 12px; margin-left: 4px; }

/* ── Section blocks in modal ────────────────────────────── */
.section-block { margin-bottom: 14px; }
.block-title {
  font-size: 12px; font-weight: 700; color: #5f79c7;
  margin-bottom: 6px; display: flex; align-items: center; gap: 6px;
  text-transform: uppercase; letter-spacing: .04em;
}
.reason-content {
  font-size: 13px; line-height: 1.65; color: #24314f;
  white-space: pre-wrap; word-break: break-word;
  background: #f7f9fc; border: 1px solid #e6ebf2;
  border-radius: 5px; padding: 12px;
  display: grid; gap: 4px;
}
.error-content { border-color: #f0d1d5; background: #fff0f1; color: #b24553; }

/* ── Responsive ─────────────────────────────────────────── */
@media (max-width: 1400px) { .summary-sections { grid-template-columns: 1fr; } }
@media (max-width: 1100px) { .decisions-section .section-cards { grid-template-columns: 1fr 1fr; } }
@media (max-width: 900px)  {
  .page-header { flex-direction: column; align-items: stretch; }
  .single-line-toolbar { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 700px)  {
  .pccd-page { padding: 16px; }
  .single-line-toolbar { grid-template-columns: 1fr; }
  .summary-section:first-child .section-cards { grid-template-columns: 1fr; }
  .decisions-section .section-cards { grid-template-columns: 1fr 1fr; }
  .pagination-bar { flex-direction: column; align-items: flex-start; }
  .queue-status-bar { flex-wrap: wrap; }
}
</style>
